import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type StaffRole = 'owner' | 'manager' | 'cashier' | 'inventory' | 'viewer';

export interface AuthUser {
  id: string;
  name: string;
  email: string;
  phone: string;
  /**
   * Primary role alias — derived từ roleIds[0] khi nhiều role.
   */
  role: StaffRole;
  roleIds: string[];
  /** Permission keys — BE resolves server-side từ role/user. Source of truth cho RBAC. */
  permissions: string[];
  tenantId: string;
  storeId?: string;
  /** Array of store_id strings — populated from user.store_assignments at login. */
  storeAssignments: string[];
  /** User's default store (User.delivery_store_id). Fallback cho owner chưa có store_assignments. */
  deliveryStoreId?: string;
}

interface AuthState {
  user: AuthUser | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
}

const initialState: AuthState = {
  user: null,
  accessToken: localStorage.getItem('erp_access_token'),
  refreshToken: localStorage.getItem('erp_refresh_token'),
  isAuthenticated: !!localStorage.getItem('erp_access_token'),
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setCredentials: (
      state,
      action: PayloadAction<{ accessToken: string; refreshToken: string; user: AuthUser }>
    ) => {
      state.accessToken = action.payload.accessToken;
      state.refreshToken = action.payload.refreshToken;
      state.user = action.payload.user;
      state.isAuthenticated = true;
      localStorage.setItem('erp_access_token', action.payload.accessToken);
      localStorage.setItem('erp_refresh_token', action.payload.refreshToken);
    },
    clearCredentials: (state) => {
      state.accessToken = null;
      state.refreshToken = null;
      state.user = null;
      state.isAuthenticated = false;
      localStorage.removeItem('erp_access_token');
      localStorage.removeItem('erp_refresh_token');
      // Single source-of-truth khi kết thúc phiên (logout / token hết hạn /
      // 401 refresh fail): xoá POS shift binding để tránh stale cross-tenant
      // store_id bị interceptor inject sang phiên mới (→ 403 STORE_ACCESS_DENIED).
      try {
        sessionStorage.removeItem('pos.shiftStoreId');
        sessionStorage.removeItem('pos.shiftStoreTenantId');
      } catch {
        /* sessionStorage unavailable (SSR / private mode) */
      }
    },
    setUser: (state, action: PayloadAction<AuthUser>) => {
      state.user = action.payload;
    },
  },
});

export const { setCredentials, clearCredentials, setUser } = authSlice.actions;
export default authSlice.reducer;
